import React, { memo, useEffect, useState, useRef } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

const CustomerCard = memo(({ customer, onImageClick }) => {
  return (
    <div className="relative group cursor-pointer flex-shrink-0 w-48">
      {/* Rectangle Frame Container */}
      <div 
        className="relative w-full aspect-[3/4]"
        onClick={() => onImageClick(customer)}
      >
        {/* Main Mirror Rectangle */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-200 shadow-2xl transform transition-all duration-500 group-hover:scale-105 group-hover:shadow-amber-300/50">
          {/* Mirror Surface */}
          <div className="absolute inset-2 rounded-md bg-gradient-to-br from-slate-100 via-white to-slate-50 overflow-hidden shadow-inner">
            {/* Customer Image */}
            <img
              src={customer.image}
              alt={customer.name}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
            />
            
            {/* Mirror Reflection Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/10 pointer-events-none"></div>
            
            {/* Additional Metal Reflection */}
            <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-gradient-to-br from-white/30 to-transparent blur-xl pointer-events-none"></div>
            
            {/* Click to expand indicator */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                <svg className="w-6 h-6 text-amber-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Enhanced Mirror Frame Highlights */}
          <div className="absolute top-0 left-1/4 w-1/2 h-6 bg-gradient-to-b from-yellow-100 to-transparent blur-md opacity-80"></div>
          <div className="absolute top-1/4 left-0 w-4 h-1/2 bg-gradient-to-r from-amber-200 to-transparent blur-sm opacity-70"></div>
          <div className="absolute top-1/4 right-0 w-4 h-1/2 bg-gradient-to-l from-amber-200 to-transparent blur-sm opacity-70"></div>
          <div className="absolute bottom-0 left-1/4 w-1/2 h-8 bg-gradient-to-t from-amber-800/40 to-transparent blur-lg"></div>
          
          {/* Hover rim glow */}
          <div className="absolute inset-0 rounded-lg ring-2 ring-amber-400/0 group-hover:ring-amber-400/60 transition-all duration-500"></div>
          
          {/* Metal beveled edge effect */}
          <div className="absolute inset-0 rounded-lg border-2 border-amber-300/50 shadow-lg"></div>
        </div>

        {/* Enhanced Outer Glow */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-400/20 to-yellow-600/20 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        {/* Subtle surface texture */}
        <div className="absolute inset-0 rounded-lg opacity-30 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, transparent 60%)'
          }}
        ></div>

        {/* Enhanced Base Shadow */}
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-amber-900/30 rounded-full blur-2xl"></div>
      </div>

      {/* Customer Info Below Card */}
      <div className="mt-3 text-center space-y-0.5">
        <h3 className="text-xs font-serif font-semibold text-amber-900 tracking-wide">
          {customer.name}
        </h3>
        {customer.location && (
          <p className="text-xs text-amber-700/70 font-light">
            {customer.location}
          </p>
        )}
        {customer.rating && (
          <div className="flex justify-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className={`text-xs ${
                  i < customer.rating ? 'text-amber-500' : 'text-gray-300'
                }`}
              >
                ★
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

const ImageModal = ({ customer, onClose }) => {
  if (!customer) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex mt-20 items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-amber-900 rounded-full p-2 shadow-lg transition-all duration-300"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image Container */}
        <div className="relative">
          <img
            src={customer.image}
            alt={customer.name}
            className="w-full h-auto max-h-[60vh] object-fit"
          />
        </div>

        {/* Customer Info */}
        <div className="p-6 bg-gradient-to-b from-amber-50 to-white border-t border-amber-200">
          <h3 className="text-2xl font-serif font-bold text-amber-900 mb-2">
            {customer.name}
          </h3>
          {customer.location && (
            <p className="text-amber-700 text-lg mb-3">
              📍 {customer.location}
            </p>
          )}
          {customer.rating && (
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className={`text-2xl ${
                    i < customer.rating ? 'text-amber-500' : 'text-gray-300'
                  }`}
                >
                  ★
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HappyCustomersSection = () => {
  const scrollContainerRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  // Sample customer data - replace with your actual data
  const customers = [
    {
      id: 1,
      name: "Diya Partha",
      location: "Kerala",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652557/WhatsApp_Image_2025-11-20_at_20.51.09_e9d1ec00_pqzcvr.jpg",
      rating: 5
    },
    {
      id: 2,
      name: "Priya",
      location: "Kerala",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652531/WhatsApp_Image_2025-11-20_at_20.51.36_c6e4958b_sr0vpn.jpg",
      rating: 5
    },
    {
      id: 3,
      name: "Sreya",
      location: "Oman",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652540/WhatsApp_Image_2025-11-20_at_20.50.54_18d14237_wrey3i.jpg",
      rating: 5
    },
    {
      id: 4,
      name: "Naomi",
      location: "Qatar",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652557/WhatsApp_Image_2025-11-20_at_20.52.35_c8959ef5_xppsln.jpg",
      rating: 5
    },
    {
      id: 5,
      name: "Nayana",
      location: "Kerala",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652530/WhatsApp_Image_2025-11-20_at_20.52.08_ca8f3001_uxud6n.jpg",
      rating: 5
    },
     {
      id: 6,
      name: "Neethi",
      location: "Kerala",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763667503/WhatsApp_Image_2025-11-21_at_01.05.37_cc12b4e6_lzujet.jpg",
      rating: 5
    },
    {
      id: 7,
      name: "Mounica",
      location: "Andhra Pradesh",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652530/WhatsApp_Image_2025-11-20_at_20.50.32_1f53005c_f6qfjz.jpg",
      rating: 5
    },
    {
      id: 8,
      name: "Sheeja K",
      location: "Kerala",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652530/WhatsApp_Image_2025-11-20_at_20.53.12_403fb063_wqqm5p.jpg",
      rating: 5
    },
    
    {
      id: 9,
      name: "Sreya Krishnan",
      location: "Oman",
      image: "https://res.cloudinary.com/dubbklkla/image/upload/v1763652530/WhatsApp_Image_2025-11-20_at_20.52.16_8faf6821_ewjxdp.jpg",
      rating: 5
    }
  ];

  // Duplicate customers for infinite scroll effect
  const duplicatedCustomers = [...customers, ...customers];

  const scroll = (direction) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    
    const cardWidth = 208; // 192px (w-48) + 16px gap
    const scrollAmount = cardWidth * 3; // Scroll 3 cards at a time
    
    if (direction === 'left') {
      scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 1200,
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    });
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !isAutoScrolling) return;

    let scrollAmount = 0;
    const scrollStep = 0.5;
    const cardWidth = 208; // 192px (w-48) + 16px gap
    const totalWidth = cardWidth * customers.length;

    const scroll = () => {
      if (!isAutoScrolling) return;
      
      scrollAmount += scrollStep;
      scrollContainer.scrollLeft = scrollAmount;

      // Reset when we've scrolled through the first set
      if (scrollAmount >= totalWidth) {
        scrollAmount = 0;
        scrollContainer.scrollLeft = 0;
      }

      requestAnimationFrame(scroll);
    };

    const animationId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationId);
  }, [isAutoScrolling, customers.length]);

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50/30 via-white to-amber-50/20 overflow-hidden">
      {/* Ambient Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Refined Header */}
        <div className="text-center mb-12" data-aos="fade-down">
          <div className="inline-block mb-4">
            <div className="flex items-center gap-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400"></div>
              <span className="text-amber-600 uppercase tracking-[0.3em] text-sm font-light">
                Testimonials
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400"></div>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-amber-900 mb-4 tracking-tight">
            HAPPY CUSTOMERS
          </h2>
          
          <p className="text-amber-800/70 text-base md:text-lg font-light max-w-2xl mx-auto leading-relaxed">
            Discover the joy and elegance our jewelry brings to cherished moments
          </p>
        </div>

        {/* Sliding Customer Cards with Navigation */}
        <div className="relative">
          {/* Left Arrow Button */}
        

          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-hidden px-12"
            onMouseEnter={() => setIsAutoScrolling(false)}
            onMouseLeave={() => setIsAutoScrolling(true)}
            style={{ scrollBehavior: 'auto' }}
          >
            {duplicatedCustomers.map((customer, index) => (
              <CustomerCard 
                key={`${customer.id}-${index}`} 
                customer={customer}
                onImageClick={setSelectedImage}
              />
            ))}
          </div>
        </div>

        {/* Bottom Accent */}
        <div className="mt-20 flex justify-center" data-aos="fade-up">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            <div className="w-16 h-px bg-gradient-to-r from-amber-400 to-transparent"></div>
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <div className="w-16 h-px bg-gradient-to-l from-amber-400 to-transparent"></div>
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal 
          customer={selectedImage} 
          onClose={() => setSelectedImage(null)} 
        />
      )}

      {/* Enhanced Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap');
        
        section {
          font-family: 'Playfair Display', serif;
        }
      `}</style>
    </section>
  );
};

export default memo(HappyCustomersSection);